import { addPreviewParameter } from './urlUtils';
import { PREVIEW_MODE_ENABLED, setPreviewMode } from './previewStorage';

function hasProtocol(value) {
  return /^[a-z][a-z0-9+.-]*:\/\//i.test(value);
}

function inferProtocolForHost(value) {
  const authority = String(value).split(/[/?#]/)[0].toLowerCase();
  const host = authority.replace(/^\[|\]$/g, '').split(':')[0];

  if (
    host === 'localhost'
    || host === '0.0.0.0'
    || host === '::1'
    || host.startsWith('127.')
    || host.endsWith('.localhost')
  ) {
    return 'http://';
  }

  return 'https://';
}

/**
 * Normalize a configured shop address. Redirect resolution belongs to the
 * selected product runtime (cloud webservice or plugin bridge), never here.
 *
 * @param {string} shopAddress - Shop address (with or without protocol)
 * @returns {Promise<string>} Absolute storefront URL
 */
export async function resolveShopUrl(shopAddress) {
  if (!shopAddress) {
    throw new Error('Shop address is required');
  }

  const trimmedAddress = String(shopAddress).trim();
  const prefix = hasProtocol(trimmedAddress) ? '' : inferProtocolForHost(trimmedAddress);
  const fullUrl = `${prefix}${trimmedAddress}`;

  return fullUrl;
}

/**
 * Build preview URL with kea-preview parameter
 * Adds the parameter after the product runtime has resolved redirects.
 *
 * @param {string} shopAddress - Shop address
 * @returns {Promise<string>} Preview URL with kea-preview=true parameter
 */
export async function buildPreviewUrl(shopAddress) {
  const finalUrl = await resolveShopUrl(shopAddress);
  return addPreviewParameter(finalUrl, 'kea-preview', 'true');
}

/**
 * Open preview in new tab
 * Adds the preview parameter and opens a new tab.
 *
 * @param {string} shopAddress - Shop address
 * @returns {Promise<void>}
 */
export async function openPreview(shopAddress) {
  if (!shopAddress) {
    throw new Error('Shop address is required');
  }

  // Mark the current browser as preview-enabled before opening the storefront.
  // If storage is unavailable, the URL param still provides a deterministic fallback.
  try {
    setPreviewMode(PREVIEW_MODE_ENABLED);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Failed to persist preview mode locally:', error);
  }

  const previewUrl = await buildPreviewUrl(shopAddress);
  window.open(previewUrl, '_blank', 'noopener,noreferrer');
}
